import React from 'react';
import { Link } from 'react-router-dom';
import { LegalPageLayout } from '../legal/LegalPageLayout';

export function Terms() {
  return (
    <LegalPageLayout
      title="Terms of Use"
      meta="Effective date: 11 August 2025 · Tarana Media Private Limited"
    >
      <section className="terms-section" id="introduction">
        <p>
          Welcome to Tarana! These Terms of Use ("Terms," "Agreement") constitute a legally
          binding contract between you ("User," "you") and Tarana Media Private Limited, a
          company incorporated under the laws of India, with its principal place of business at
          Hyderabad, Telangana ("Tarana," "we," "us," or "our").
        </p>
        <p>
          By accessing, browsing, registering for an account, or using any part of the Tarana
          website, mobile applications, APIs, or any other services provided by Tarana
          (collectively, the "Services"), you acknowledge that you have read, understood, and
          agree to be bound by these Terms. If you do not agree to these Terms, you may not access
          or use the Services.
        </p>
        <p>
          Tarana is an all-in-one music platform designed to connect Creators, which include
          individuals and organizations in the music industry, and Non-Creators, such as listeners
          or fans, within a single ecosystem. Our Services empower Creators to collaborate,
          freelance, manage a team of Creators, create, share, and monetize their music and video
          content. Non-Creators can engage with creators, stream music and videos, and directly
          hire artists or bands for performances, projects, or custom work.
        </p>
      </section>

      <section className="terms-section" id="definitions">
        <h2 className="terms-h2">1. Definitions</h2>
        <p>For the purpose of these Terms, the following definitions apply:</p>
        <ul className="terms-list">
          <li>
            <strong>"Tarana"</strong> refers to Tarana Media Private Limited.
          </li>
          <li>
            <strong>"Account"</strong>: A user account created on the Tarana Platform.
          </li>
          <li>
            <strong>"Artist"</strong>: A User who utilizes the Services to create, upload, share,
            collaborate on, freelance and monetize their original music and video content, and/or
            offers their services for direct hiring.
          </li>
          <li>
            <strong>"Band"</strong>: A designated group of Users (typically Artists) formed on the
            Platform for the purpose of co-creating, collaborating on, freelance, uploading,
            sharing, and potentially monetizing music and video Content as a team.
          </li>
          <li>
            <strong>"Band Content"</strong>: Any Content created, uploaded, or contributed by
            members within a Band on the Platform.
          </li>
          <li>
            <strong>"Content"</strong>: Any and all audio, video, text, graphics, images, data,
            information, and other materials or works that are uploaded, submitted, posted,
            displayed, or otherwise made available on or through the Services.
          </li>
          <li>
            <strong>"Listener"</strong>: A User who primarily engages with Artists, streams audio
            and video content, builds playlists, and/or directly hires Artists for performances,
            projects, or custom work.
          </li>
          <li>
            <strong>"Organization"</strong>: An entity (e.g., record label, studio, event
            organizer) that uses the Services to upload, share, manage teams, coordinate projects,
            and/or collaborate with Artists.
          </li>
          <li>
            <strong>"Platform"</strong>: The Tarana website, mobile applications, and any other
            digital properties or interfaces through which the Services are provided.
          </li>
          <li>
            <strong>"Services"</strong>: All products, services, features, technologies, content,
            and functionalities offered by Tarana, including but not limited to content creation
            and uploading, collaboration tools, freelance marketplace, payment processing, and
            music and video content sharing and streaming.
          </li>
          <li>
            <strong>"User"</strong>: Any individual or entity that accesses or uses any part of the
            Services, including Artists, Listeners, and Organizations.
          </li>
          <li>
            <strong>"User-Generated Content" (UGC)</strong>: Any Content uploaded, submitted,
            posted, displayed, or otherwise made available by Users on or through the Services.
          </li>
        </ul>
      </section>

      <section className="terms-section" id="account-creation">
        <h2 className="terms-h2">2. Account Creation and Eligibility</h2>

        <h3 className="terms-h3">2.1. Eligibility Requirements</h3>
        <p>To create an Account and use the Services, you must:</p>
        <ul className="terms-list">
          <li>
            Be at least 13 years of age. If you are between 13 and 18 years of age (or the age of
            majority in your jurisdiction), you must have the explicit consent of your parent or
            legal guardian to use the Services, and your parent or legal guardian must agree to
            these Terms on your behalf.
          </li>
          <li>
            Be at least 18 years of age (or the age of majority in your jurisdiction) to engage in
            any financial transactions, including freelancing, accepting payments, or participating
            in monetization features.
          </li>
          <li>Have the legal capacity to enter into a binding contract with Tarana.</li>
          <li>Comply with all applicable laws and regulations in your jurisdiction.</li>
        </ul>

        <h3 className="terms-h3">2.2. Account Registration</h3>
        <p>When creating an Account, you agree to:</p>
        <ul className="terms-list">
          <li>
            Provide accurate, current, and complete information as prompted by the registration
            forms.
          </li>
          <li>
            Maintain and promptly update your Account information to keep it accurate, current, and
            complete.
          </li>
          <li>
            Not create an Account for another person or entity unless you are an authorized
            employee or agent acting on behalf of a legitimate business entity.
          </li>
          <li>
            Not create multiple Accounts for yourself or on behalf of others without our express
            permission.
          </li>
        </ul>

        <h3 className="terms-h3">2.3. Account Security</h3>
        <p>
          You are solely responsible for maintaining the confidentiality and security of your
          Account credentials, including your username and password. You agree to:
        </p>
        <ul className="terms-list">
          <li>Not share your password or Account access with any third party.</li>
          <li>
            Immediately notify Tarana of any unauthorized use of your Account or any other breach of
            security.
          </li>
          <li>
            Accept full responsibility for all activities that occur under your Account, whether or
            not such activities were authorized by you.
          </li>
        </ul>
      </section>

      <section className="terms-section" id="user-rights">
        <h2 className="terms-h2">3. User Rights and Responsibilities</h2>

        <h3 className="terms-h3">3.1. General User Obligations</h3>
        <p>All Users of the Services agree to:</p>
        <ul className="terms-list">
          <li>
            Comply with these Terms of Use, our <Link to="/privacy-policy">Privacy Policy</Link>,{" "}
            <Link to="/community-guidelines">Community Guidelines</Link>,{" "}
            <Link to="/content-policy">Content Policy</Link> and all applicable local, national, and
            international laws and regulations.
          </li>
          <li>
            Use the Services only for their intended purpose and in a manner that does not interfere
            with the proper functioning of the Services or infringe upon the rights of others.
          </li>
          <li>Not engage in any fraudulent, deceptive, or misleading activities.</li>
          <li>
            Not impersonate any person or entity, or falsely state or otherwise misrepresent your
            affiliation with a person or entity.
          </li>
          <li>
            Not attempt to circumvent any security measures or technological protections implemented
            by Tarana.
          </li>
          <li>
            Not manipulate play counts, downloads, ratings, or reviews through automated means
            (e.g., bots, scripts) or by providing or accepting compensation or incentive.
          </li>
          <li>
            Not scrape, copy, or monitor any portion of the Content or Services using automated
            processes or similar manual processes without express written permission.
          </li>
        </ul>

        <h3 className="terms-h3">3.2. Rights and Responsibilities of Artists</h3>
        <p>As an Artist, you have the right to:</p>
        <ul className="terms-list">
          <li>
            Create, upload, share, and promote your original music and video Content on the
            Platform.
          </li>
          <li>
            Utilize Tarana's tools for content creation, distribution, and engagement.
          </li>
          <li>
            Monetize your Content through various avenues, including subscriptions, ad revenue
            sharing, and freelancing, subject to Tarana's monetization terms.
          </li>
          <li>
            Participate in collaborative projects, including forming or joining Bands with other
            Users.
          </li>
        </ul>
        <p>As an Artist, you are responsible for:</p>
        <ul className="terms-list">
          <li>
            Warranting that you own or have obtained all necessary rights, licenses, consents, and
            permissions for any Content you upload, ensuring it does not infringe upon the
            intellectual property or other rights of any third party.
          </li>
          <li>Complying with Tarana's Content guidelines and Community Standards.</li>
          <li>
            Fulfilling your contractual obligations when engaged for freelancing, including
            delivering work as agreed and adhering to agreed-upon timelines and specifications.
          </li>
          <li>
            Providing accurate and complete payment and tax information as required for monetization
            purposes.
          </li>
        </ul>

        <h3 className="terms-h3">3.3. Rights and Responsibilities of Organizations</h3>
        <p>As an Organization, you have the right to:</p>
        <ul className="terms-list">
          <li>Upload, share, and manage your Content on the Platform.</li>
          <li>Manage internal team and coordinate projects within the Platform's environment.</li>
          <li>
            Collaborate directly with Artists or other Users, or manage Bands to coordinate projects
            and collaborations.
          </li>
          <li>Hire Artists for various music related works.</li>
        </ul>
        <p>As an Organization, you are responsible for:</p>
        <ul className="terms-list">
          <li>
            Ensuring that all your team members comply with these Terms and Tarana's policies.
          </li>
          <li>Obtaining all necessary rights and licenses for any Content you upload or manage.</li>
          <li>
            Complying with Know Your Business (KYC) requirements for any transactions or team
            management, as detailed in Section 4.3.
          </li>
          <li>Fulfilling your contractual obligations when engaging Artists through the Platform.</li>
        </ul>

        <h3 className="terms-h3">3.4. Rights and Responsibilities of Listeners</h3>
        <p>As a Listener, you have the right to:</p>
        <ul className="terms-list">
          <li>Stream audio and video Content for personal, non-commercial use.</li>
          <li>Create and manage playlists.</li>
          <li>Engage with creators through various Platform features.</li>
          <li>
            Directly hire creators for private or commercial engagements, performances, or custom
            creative work.
          </li>
          <li>Report any inappropriate Content or conduct you encounter on the Platform.</li>
        </ul>
        <p>As a Listener, you are responsible for:</p>
        <ul className="terms-list">
          <li>
            Strictly complying with Content usage rules, including refraining from unauthorized
            redistribution of Content.
          </li>
          <li>Respecting the intellectual property rights of the creator and other Users.</li>
          <li>
            Promptly paying for services rendered when hiring Artists through the Platform.
          </li>
          <li>
            Complying with Know Your Customer (KYC) requirements for any hiring or payment
            transactions, as detailed in Section.
          </li>
        </ul>
      </section>

      <section className="terms-section" id="content-ownership">
        <h2 className="terms-h2">4. Content Ownership and Licensing</h2>

        <h3 className="terms-h3">4.1. Ownership of User-Generated Content (UGC)</h3>
        <p>
          You retain all ownership rights in your original Content that you upload, submit, post, or
          display on or through the Services ("Your Content"). Tarana does not claim any ownership
          rights over Your Content. Content made available on the Services by other Users remains the
          property of those respective Users and is subject to their intellectual property rights.
          You do not acquire ownership of another User's Content merely by reposting it or including
          it in playlists.
        </p>

        <h3 className="terms-h3">4.2. Licenses Granted</h3>
        <h4 className="terms-h4">4.2.1. License Granted to Tarana</h4>
        <p>
          By submitting Your Content through the Services, you grant Tarana a worldwide,
          non-exclusive, royalty-free, sublicensable, and transferable license to access, use,
          reproduce, distribute, publicly display, publicly perform, adapt, synchronize, prepare
          derivative works of, compile, make available, and communicate Your Content in connection
          with the Services and Tarana's business, including for advertising and promotional
          material. This license also includes the right to use your trademarks, service marks,
          slogans, and logos solely for the marketing and promotion of Tarana's Services. This robust
          licensing framework is essential for Tarana's operational flexibility and its ability to
          innovate and expand its service offerings.
        </p>
        <h4 className="terms-h4">4.2.2. License Granted to Other Users</h4>
        <p>
          You also grant other Users of the Services a worldwide, non-exclusive, royalty-free,
          sublicensable, and transferable license to access and use Your Content, but only as
          enabled by a feature of the Service and in accordance with your Content sharing settings.
          This conditional license is crucial for standard streaming and engagement functionalities,
          ensuring Content consumption occurs within the Platform's controlled environment.
        </p>
        <p>
          For collaborative projects, including Content created within a Band, existing and future
          members are granted a license to copy, download, modify, play, perform, and potentially
          commercialize that Content within the scope of the Band's activities on the Platform.
        </p>
        <p>
          In the context of freelancing, the specific license for the delivered work is determined
          by the hiring agreement negotiated directly between the Artist and the
          Listener/Organization. Tarana acts solely as a facilitator for these direct agreements,
          not as a licensor of the commissioned work.
        </p>
        <h4 className="terms-h4">4.2.3. Termination of Licenses</h4>
        <p>
          The licenses granted to Tarana and other Users generally terminate within a commercially
          reasonable time after you remove or delete Your Content or terminate your Account. However,
          licenses do not terminate for Content you have shared with others (including in a
          collaborative project or a Band) if they have not removed it from the Services.
        </p>
        <p>
          For user comments you submit (such as reviews, feedback, or public posts), the license is
          perpetual and irrevocable unless the Creator or Artist who owns the Content decides to
          delete those comments. Creators and Artists have the right to moderate and remove comments
          under their own Content at any time. If a comment is deleted by you or by the Creator, the
          license for that comment ends.
        </p>

        <h3 className="terms-h3">4.3. Music-Specific Licensing Considerations</h3>
        <p>
          The music industry involves a complex web of rights. For Content uploaded to Tarana,
          multiple layers of rights may be involved, including:
        </p>
        <ul className="terms-list">
          <li>Master Use Rights: Pertaining to the specific sound recording.</li>
          <li>
            Public Performance Rights: Related to the underlying musical composition (melody,
            lyrics).
          </li>
          <li>Mechanical Rights: For reproducing and distributing a song.</li>
          <li>Synchronization Rights: Required when music is combined with visual media.</li>
        </ul>
        <p>
          You explicitly warrant that you have obtained all necessary rights for your uploaded
          Content, covering both the composition and the master recording, for all intended uses on
          the Platform (e.g., sharing, promotion, sublicensing to Tarana, and potential freelancing
          scenarios). For commissioned work through freelancing, we advise Artists and hiring parties
          to explicitly define the scope of licenses (e.g., synchronization, mechanical, public
          performance) for the delivered work in their private hiring agreements.
        </p>

        <h3 className="terms-h3">4.4. Moral Rights and Attribution</h3>
        <p>
          While Users generally retain their moral rights, you agree, to the extent permitted by
          applicable law, to waive certain moral rights for specific Platform uses, such as the right
          to object to minor technical modifications or to be identified as the author in every
          instance of Platform use. Tarana is committed to providing attribution to creators where
          feasible and commercially reasonable, balancing legal requirements with Platform
          functionality and user experience.
        </p>

        <h3 className="terms-h3">4.5. Band Content Specifics</h3>
        <h4 className="terms-h4">4.5.1. Ownership and Internal Agreements</h4>
        <p>
          While individual Users retain ownership of their original contributions to Band Content,
          Tarana provides integrated features to manage revenue splits and ownership percentages for
          Band Content directly within the Platform. All revenue generated from Band Content will be
          split and distributed among Band members according to the percentages configured using the
          Platform's tools. Tarana will facilitate the distribution of these revenues based on the
          agreed-upon splits. For other internal team dynamics, specific usage rights for individual
          contributions not managed by Tarana's system, and individual responsibilities within the
          Band, members may choose to enter into a separate, explicit written agreement ("Band
          Agreement") outside of these Terms. Tarana's role is to implement the revenue and ownership
          configurations set by the Band members on the Platform.
        </p>
        <h4 className="terms-h4">4.5.2. Usage and Access of Band Content</h4>
        <p>
          When you upload or create Content within a Band, only the credited contributors retain
          ownership of their specific contributions. Neither existing nor future Band members
          automatically receive rights to that Content unless such rights are explicitly granted by
          the credited contributors. It is entirely up to the Band members to decide, assign, and
          document rights among themselves, which may be done through a separate agreement outside
          the Platform. Tarana does not facilitate or manage such rights agreements; it is the sole
          responsibility of Band members to establish and manage them.
        </p>
        <p>
          Any use of Band Content — including copying, downloading, modifying, playing, performing,
          or commercializing — is permitted only under the rights granted by the credited
          contributors. In the case of Collaborative Content with multiple credited contributors, no
          single contributor may unilaterally grant rights to the work without the consent of the
          other credited contributors, unless such authority is defined in a separate agreement
          outside the Platform.
        </p>
        <p>
          Any Band member may propose changing the setting on Band Content to allow “forking,”
          meaning other Users may create derivative works from it, but this action must be approved
          according to the Band’s own agreed-upon process. The Band is solely responsible for
          reaching a clear agreement among its members before making any Band Content “forkable.”
          Uploading Content without securing proper rights or permissions from all credited
          contributors is strictly prohibited and may result in removal of the Content and/or account
          suspension.
        </p>
        <h4 className="terms-h4">4.5.3. Leaving a Band and Band Termination</h4>
        <p>
          If you leave a Band, any Content you created or uploaded to that Band remains available for
          use by the remaining members only under the rights you explicitly granted while you were a
          member. In the case of Collaborative Content, it is entirely up to the Band members to
          determine whether rights are extended to remaining or future members, and Tarana will not
          assign these rights. You may not be able to delete Content once it has been uploaded,
          created, or posted within the Band, even if it incorporates your original work.
        </p>
        <p>
          Only the designated Band Owner can break up or delete a Band. Once a Band is deleted, the
          Band will cease to exist, and all associated Band Content will be removed from Tarana. Users
          are responsible for exporting and saving any Band Content to their local devices if they
          wish to ensure unrestricted access to it after a Band is deleted.
        </p>
      </section>

      <section className="terms-section" id="freelancing-payments">
        <h2 className="terms-h2">5. Freelancing, Payments, and Verification</h2>

        <h3 className="terms-h3">5.1. Freelancing Process and Contractual Framework</h3>
        <p>
          Tarana serves as a marketplace facilitator, connecting Listeners, Individuals, and
          Organizations (acting as "Clients") with Individuals and/or Bands (acting as "Service
          Providers") for specific engagements. For every freelance project, Tarana manages the
          entire process. This includes providing standard project agreements, offering project
          management tools, and securely handling all payments through the Platform's escrow system.
          Users understand that Tarana acts as a facilitator and neutral third party to help ensure
          freelance projects run smoothly, but does not guarantee the outcome or quality of the
          services provided. A typical freelance project involves posting a project request,
          receiving proposals, selecting a Freelancer, defining milestones and deliverables, and
          completing approvals and payments.
        </p>

        <h3 className="terms-h3">5.2. Payments and Escrow Protection</h3>
        <p>
          All payments for freelance work must be made only through Tarana. Offering or accepting
          payments outside the Platform is not allowed and violates these Terms.
        </p>
        <p>
          Tarana uses an escrow system to protect both Clients and Freelancers. When a Client accepts
          a project or milestone, the agreed payment is securely held by Tarana until the Freelancer
          delivers the work and the Client approves it. Funds are released to the Freelancer once the
          Client confirms the work is complete, or automatically after a set time if there is no
          response or request for changes.
        </p>
        <p>
          Freelancers can withdraw their earnings to the verified bank account or UPI ID provided
          during KYC verification. Tarana will clearly show any fees, payout timelines, and minimum
          withdrawal amounts on the Platform.
        </p>

        <h3 className="terms-h3">5.3. Verification and Compliance</h3>
        <h4 className="terms-h4">5.3.1. Know Your Customer (KYC) for Individuals</h4>
        <p>
          Know Your Customer (KYC) verification is mandatory for any individual Creators who wish to
          collaborate, hire, or accept payments through Tarana. This process involves collecting and
          verifying essential identity information, including full name, date of birth, residential
          address, and government-issued identification (PAN Card). Users must also provide a clear
          live photo during the verification process to confirm that they match the submitted
          documents. Verification may include document uploads, and screening against global
          sanctions lists and watchlists to prevent illicit activities. Continuous monitoring of User
          transactions and activities will be conducted to identify any suspicious behavior or
          changes in risk profiles.
        </p>
        <h4 className="terms-h4">5.3.2. Know Your Customer (KYC) for Organizations</h4>
        <p>
          For Organizations engaging in transactions, managing teams, or coordinating projects on
          Tarana, Know Your Customer (KYC) verification is mandatory. This process involves verifying
          the legal name of the company, its registered address, incorporation details, business
          licenses, and bank details. A critical component of KYC is the identification and
          verification of a key person associated with the organization, ensuring transparency of
          corporate structure. Both the Organization and its identified key person will be screened
          against sanctions, watchlists, and adverse media databases to assess potential risks.
        </p>
        <h4 className="terms-h4">5.3.3. Regulatory Compliance</h4>
        <p>
          Tarana's verification processes are designed to comply with international Anti-Money
          Laundering (AML) and Counter-Terrorist Financing (CTF) standards, such as those recommended
          by the Financial Action Task Force (FATF). Furthermore, specific attention is paid to local
          regulations, including India's Payment and Settlement Systems Act, 2007, and associated AML
          compliance requirements. The Terms will emphasize that KYC/AML requirements may vary by
          jurisdiction and are subject to ongoing changes in the regulatory landscape.
        </p>

        <h3 className="terms-h3">5.4. Fees, Taxes, and Payout Schedules</h3>
        <p>
          Tarana's Terms will clearly outline all Platform fees for various Services, including
          subscription models, ad revenue sharing, and commissions applied to freelancing
          engagements. Users are explicitly informed that they are solely responsible for all
          applicable taxes on their earnings derived from the Platform. Detailed payout schedules,
          specifying the timing for funds to become available (e.g., a clearing period after project
          completion or Client approval), and any minimum withdrawal amounts, will be provided.
        </p>
      </section>

      <section className="terms-section" id="cancellation-refund">
        <h2 className="terms-h2">6. Cancellation and Refund</h2>
        <p>
          Tarana believes in supporting its users and collaborators to the fullest extent possible
          and therefore maintains a fair cancellation and refund policy.
        </p>

        <h3 className="terms-h3">6.1. Cancellation Requests</h3>
        <p>
          Cancellations will be considered only if the request is made within the same day of placing
          the order or booking. However, a cancellation request may not be accepted if work has
          already started, such as when content has been delivered to collaborators, production has
          begun, or a booking has been confirmed with a third party.
        </p>

        <h3 className="terms-h3">6.2. Non-Cancellable Services</h3>
        <p>
          Tarana does not accept cancellation requests for time-bound or non-reversible services such
          as completed digital content delivery, session bookings that have already been executed, or
          payments made to freelancers once work has commenced.
        </p>

        <h3 className="terms-h3">6.3. Damaged, Defective, or Unsatisfactory Service</h3>
        <p>
          If you believe that the service delivered (e.g., a digital file, collaboration output, or
          booked performance) is defective or does not meet agreed expectations, please report it to
          our Customer Service team within the same day of delivery or completion. Our team will
          review the issue with the involved parties and determine an appropriate resolution, which
          may include revision, replacement, or partial refund.
        </p>

        <h3 className="terms-h3">6.4. Products with Third-Party Warranties</h3>
        <p>
          If a purchased product or service includes a warranty or guarantee from a third-party
          provider (such as instruments, merchandise, or licensed software), please direct any claims
          or service requests to that provider according to their warranty terms.
        </p>

        <h3 className="terms-h3">6.5. Refund Processing Time</h3>
        <p>
          In case of any refunds approved by Tarana, it will take approximately 6–8 business days for
          the amount to be processed and credited back to the customer’s original payment method.
        </p>
      </section>

      <section className="terms-section" id="collaboration">
        <h2 className="terms-h2">7. Collaboration and Band Projects</h2>

        <h3 className="terms-h3">7.1. Rules for Collaborative Content Creation and Ownership</h3>
        <p>
          Tarana fosters a dynamic environment for creative collaboration, including the formation of
          'Bands' where groups of Users can co-create, upload, and manage music and video content as
          a team. 'Collaborative Content' refers to any Content created jointly by two or more Users
          within Tarana's designated collaboration features, including Band Content.
        </p>
        <p>
          For aspects of collaboration not directly managed by Tarana's integrated ownership and
          revenue split features (as detailed in Section 4.5.1), the Terms will strongly recommend,
          or potentially require, that collaborators (including Band members) enter into a separate,
          explicit written agreement, often referred to as a 'Collaboration Agreement' or 'Band
          Agreement,' outside of Tarana's general Terms and Conditions.
        </p>
        <p>
          This external agreement should meticulously detail critical aspects such as specific usage
          rights granted to each collaborator, and individual responsibilities within the project or
          Band. Tarana's role in this context is to provide the collaborative tools and Platform
          infrastructure; it will not mediate or adjudicate disputes arising from these internal
          collaboration agreements between Users.
        </p>

        <h3 className="terms-h3">7.2. Management and Deletion of Collaborative Content</h3>
        <p>
          Content uploaded to or created within a collaborative project or a Band on Tarana is
          accessible to current members only under the rights explicitly granted by the credited
          contributors. Neither existing nor future members automatically receive rights to Content
          created before they joined unless such rights are granted by the credited contributors. In
          the case of Collaborative Content with multiple credited contributors, it is entirely up to
          the Band or project members to decide, assign, and document rights among themselves. No
          single contributor may unilaterally grant rights to the work without the consent of the
          other credited contributors, unless such authority is defined in a separate agreement
          outside the Platform.
        </p>
        <p>
          If a User leaves a collaboration or a Band, their contributions typically remain available
          for use only under the rights they and any other credited contributors granted before
          departure. The Terms will define who has the authority to make Collaborative Content
          “public” or “forkable” (allowing derivative works), which may be the designated project
          owner or require mutual consent from all credited contributors. The process for deleting
          collaborative projects or Content will also be outlined, specifying that only the
          designated project owner or Band Owner typically has the authority to initiate deletion,
          resulting in the removal of that Content from Tarana’s Platform.
        </p>

        <h3 className="terms-h3">7.3. Dispute Resolution for Collaboration-Related Issues</h3>
        <p>
          For disputes arising within collaborative projects or Bands, Tarana will encourage
          collaborators to first attempt to resolve issues directly amongst themselves, relying on
          their private agreements. While Tarana may offer basic internal dispute resolution
          mechanisms, such as structured messaging tools or guided mediation prompts, for
          collaboration-related or Band-related issues, it will explicitly state that it does not act
          as a legal arbiter for these private agreements. The Terms will clearly communicate that
          Tarana is not responsible for enforcing the terms of private collaboration agreements
          between Users, reinforcing its role as a facilitating platform rather than a legal
          intermediary in such disputes.
        </p>
      </section>

      <section className="terms-section" id="community-standards">
        <h2 className="terms-h2">8. Community Standards and Content Moderation</h2>

        <h3 className="terms-h3">8.1. Prohibited Conduct and Content Guidelines</h3>
        <p>
          Tarana's Terms will clearly define categories of prohibited conduct and Content to ensure a
          positive User experience and adherence to legal norms. This includes, but is not limited
          to:
        </p>
        <ul className="terms-list">
          <li>
            <strong>Unlawful Content:</strong> Any material that is illegal under the laws of
            relevant jurisdictions.
          </li>
          <li>
            <strong>Harmful/Abusive Content:</strong> Hate speech, harassment, cyberbullying,
            violence, terrorism, or illegal activities.
          </li>
          <li>
            <strong>Explicit/Obscene Content:</strong> Pornographic, obscene, or sexually explicit
            material, especially content harmful to minors.
          </li>
          <li>
            <strong>Infringing Content:</strong> Material that violates the intellectual property
            rights of others, including copyrights and trademarks.
          </li>
          <li>
            <strong>Misinformation/Deceptive Practices:</strong> Content that is misleading,
            deceptive, or intended to scam or defraud other Users, including attempts to generate fake
            engagement or manipulate Platform metrics.
          </li>
          <li>
            <strong>Privacy Violations:</strong> Content that is invasive of another individual's
            privacy.
          </li>
          <li>
            <strong>Spam:</strong> Unsolicited or repetitive communications.
          </li>
        </ul>
        <p>
          These standards apply to all User-Generated Content across the Platform, including uploaded
          music and video, comments, profile information, and any other User submissions.
        </p>

        <h3 className="terms-h3">8.2. Content Reporting and Takedown Procedures</h3>
        <p>
          Tarana provides clear and accessible mechanisms for Users to report content that violates
          these Terms or Community Guidelines. The Platform will employ a multi-layered approach to
          content moderation:
        </p>
        <ul className="terms-list">
          <li>
            <strong>Automated Flagging:</strong> Utilizing advanced Artificial Intelligence (AI) and
            Machine Learning (ML) algorithms to proactively detect and filter potentially harmful or
            infringing Content, especially at the point of upload.
          </li>
          <li>
            <strong>Human Review:</strong> A team of trained human moderators reviews Content flagged
            by automated systems or User reports, particularly for borderline cases that require
            nuanced judgment.
          </li>
          <li>
            <strong>Copyright Takedown Process:</strong> If Users believe their copyright has been
            infringed, they can use the standard reporting tool to submit a copyright claim. Tarana
            will review these reports under established takedown procedures and remove infringing
            content if a valid notice is received.
          </li>
          <li>
            <strong>Counter-Notification Process:</strong> Users whose Content has been removed due to
            a copyright takedown notice will have a mechanism to dispute the notice by submitting a
            counter-notification, allowing for re-evaluation of the claim.
          </li>
        </ul>

        <h3 className="terms-h3">8.3. Blocking and Banning Policy for Violators</h3>
        <p>
          Tarana reserves the right to remove or disable access to any User Content for any reason
          deemed necessary to uphold Platform integrity and safety. Consequences for violations will
          be clearly outlined, ranging from Content removal and temporary Account suspension to
          permanent termination of Accounts. Criteria for severe abuse or repeated violations leading
          to immediate or permanent Account termination will be specified, ensuring consistency and
          fairness in enforcement.
        </p>

        <h3 className="terms-h3">8.4. Intermediary Liability and Safe Harbor Provisions</h3>
        <p>
          Tarana operates as an "intermediary" or "hosting service provider" for User-Generated
          Content. The Platform acknowledges its obligations under relevant international regulations,
          including the European Union's Digital Services Act (DSA) and India's Information Technology
          (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021 (IT Rules). These
          regulations impose significant due diligence obligations on intermediaries, including strict
          takedown timelines and the potential loss of "safe harbor" protections for non-compliance.
          Tarana's policies will be designed to navigate these complex and evolving regulatory
          landscapes, ensuring compliance while protecting the Platform.
        </p>

        <h3 className="terms-h3">8.5. Balancing Free Speech with Platform Integrity and Safety</h3>
        <p>
          Tarana is committed to fostering an environment that supports free expression while
          simultaneously maintaining Platform integrity and User safety. Our Content moderation
          policies are meticulously designed to strike a balance between these principles, guided by
          legal obligations and community standards. This involves allowing a broad spectrum of ideas
          to flourish while actively preventing the dissemination of harmful, illegal, or abusive
          Content.
        </p>
      </section>

      <section className="terms-section" id="liability">
        <h2 className="terms-h2">9. Limitations of Liability and Disclaimers</h2>

        <h3 className="terms-h3">9.1. "As Is" Basis of Service Provision</h3>
        <p>
          Tarana's Services are provided on an "as is" and "as available" basis, meaning the Platform
          does not guarantee uninterrupted, error-free, or fully secure operation. This standard
          legal provision is crucial for managing User expectations regarding service performance and
          availability in a dynamic online environment.
        </p>

        <h3 className="terms-h3">9.2. Exclusion of Warranties</h3>
        <p>
          Tarana explicitly disclaims all express, implied, and statutory warranties, including, but
          not limited to, warranties of satisfactory quality, merchantability, fitness for a
          particular purpose, title, and non-infringement. Furthermore, Tarana explicitly states that
          it makes no warranty regarding the accuracy, reliability, or availability of Content
          provided by other Users, as it acts as an intermediary for such Content. This disclaimer
          reinforces that Users rely on User-Generated Content at their own risk.
        </p>

        <h3 className="terms-h3">9.3. User Indemnification of Tarana</h3>
        <p>
          You agree to indemnify, defend, and hold Tarana and its affiliates, directors, officers,
          employees, agents, contractors, and licensors ("Indemnitees") harmless from and against any
          and all claims, damages, losses, and expenses (including reasonable attorneys' fees and
          costs) arising out of or related to your: (a) breach of these Terms; (b) User-Generated
          Content; (c) activities on the Platform; (d) violation of any law or the rights of a third
          party; or (e) any dispute with another User. This indemnification clause is particularly
          vital for claims related to intellectual property infringement stemming from Content
          uploaded by Users, shifting the legal burden from the Platform to the responsible party.
        </p>

        <h3 className="terms-h3">9.4. Scope of Tarana's Liability</h3>
        <p>
          Tarana's liability will be significantly limited. We will not be liable for indirect,
          incidental, special, punitive, exemplary, or consequential damages, such as loss of
          profits, data, or business interruption. Tarana is also not liable for Content provided by
          other Users or for any third-party applications, products, or services accessed through or
          in connection with Tarana. Where permissible by law, Tarana may cap its maximum liability to
          a specific monetary amount or to the amount paid by the User for the Services. It is
          important to acknowledge, however, that certain statutory limitations on liability, such as
          those for death or personal injury caused by negligence or for fraud, cannot be excluded by
          law.
        </p>
      </section>

      <section className="terms-section" id="privacy-data-protection">
        <h2 className="terms-h2">10. Privacy and Data Protection</h2>
        <p>
          Tarana is committed to protecting your privacy. All matters pertaining to the collection,
          use, processing, and sharing of your data are governed by our separate and comprehensive
          Privacy Policy. By using the Services, you acknowledge that you have read, understood, and
          agree to our <Link to="/privacy-policy">Privacy Policy</Link>, which is incorporated into
          these Terms by reference. We encourage you to review our{" "}
          <Link to="/privacy-policy">Privacy Policy</Link> to understand our data protection
          practices, your data rights, and the security measures we implement to safeguard your
          information, including sensitive KYC data.
        </p>
      </section>

      <section className="terms-section" id="force-majeure">
        <h2 className="terms-h2">11. Force Majeure &amp; Service Changes</h2>
        <p>
          Neither party shall be liable for delays or failures caused by events beyond its reasonable
          control (e.g., natural disasters, war, cyber‑attacks).
        </p>
        <p>
          Tarana reserves the right to modify, suspend, or discontinue any feature at any time, with
          notice to users if feasible.
        </p>
      </section>

      <section className="terms-section" id="governing-law">
        <h2 className="terms-h2">12. Governing Law and Dispute Resolution</h2>

        <h3 className="terms-h3">12.1. Choice of Governing Law</h3>
        <p>
          These Terms and all disputes arising out of or related to them or the Services shall be
          governed by and construed in accordance with the laws of India, without regard to its
          conflict of law principles.
        </p>

        <h3 className="terms-h3">12.2. Jurisdiction and Forum Selection</h3>
        <p>
          You agree that any legal action or proceeding arising out of or related to these Terms or
          the Services shall be brought exclusively in the courts located in India. You hereby consent
          to the personal jurisdiction of such courts and waive any objection as to inconvenient
          forum.
        </p>

        <h3 className="terms-h3">12.3. Multi-Tiered Dispute Resolution Mechanisms</h3>
        <p>
          To provide efficient and effective avenues for resolving disputes, Tarana implements a
          multi-tiered dispute resolution framework:
        </p>
        <ul className="terms-list">
          <li>
            <strong>Initial Internal Resolution:</strong> Users are encouraged to first attempt to
            resolve any disputes directly through Tarana's internal customer support channels or
            messaging systems. This initial step aims to address minor issues quickly and amicably.
          </li>
          <li>
            <strong>Mediation:</strong> If internal resolution efforts are unsuccessful, the Terms
            mandate non-binding mediation as the next step. Mediation involves a neutral third party
            facilitating communication between the disputing parties to help them reach a mutually
            agreeable settlement.
          </li>
          <li>
            <strong>Binding Arbitration:</strong> For disputes that remain unresolved after
            mediation, these Terms specify binding arbitration as the final and exclusive method of
            dispute resolution, particularly for commercial disputes. The arbitration shall be
            conducted in the English language, in accordance with the Arbitration and Conciliation
            Act, 1996. The seat and venue of arbitration shall be Hyderabad, India. The arbitral award
            shall be final and binding on the parties and may be entered and enforced in any court of
            competent jurisdiction.
          </li>
          <li>
            <strong>Exceptions:</strong> Notwithstanding the foregoing, Tarana reserves the right to
            seek injunctive or other equitable relief in any court of competent jurisdiction to
            prevent the actual or threatened infringement, misappropriation, or violation of its
            copyrights, trademarks, trade secrets, patents, or other intellectual property rights.
          </li>
        </ul>
      </section>

      <section className="terms-section" id="miscellaneous">
        <h2 className="terms-h2">13. Miscellaneous</h2>

        <h3 className="terms-h3">13.1. Severability</h3>
        <p>
          If any provision of these Terms is found to be invalid or unenforceable by a court of
          competent jurisdiction, that provision shall be enforced to the maximum extent permissible,
          and the other provisions of these Terms will remain in full force and effect.
        </p>

        <h3 className="terms-h3">13.2. Entire Agreement</h3>
        <p>
          These Terms, together with our <Link to="/privacy-policy">Privacy Policy</Link>,{" "}
          <Link to="/content-policy">Content Policy</Link> and{" "}
          <Link to="/community-guidelines">Community Guidelines</Link>, constitute the entire and
          exclusive understanding and agreement between Tarana and you regarding the Services, and
          these Terms supersede and replace all prior oral or written understandings or agreements
          between Tarana and you regarding the Services.
        </p>

        <h3 className="terms-h3">13.3. Amendments</h3>
        <p>
          Tarana reserves the right to modify or revise these Terms at any time, in its sole
          discretion. We will provide notice of any material changes by posting the updated Terms on
          the Platform and updating the "Last Updated" date. Your continued use of the Services after
          such modifications constitutes your acceptance of the revised Terms. If you do not agree to
          the revised Terms, you must stop using the Services.
        </p>

        <h3 className="terms-h3">13.4. Assignment</h3>
        <p>
          You may not assign or transfer these Terms, by operation of law or otherwise, without
          Tarana's prior written consent. Any attempt by you to assign or transfer these Terms,
          without such consent, will be null and void. Tarana may freely assign or transfer these
          Terms without restriction. Subject to the foregoing, these Terms will bind and inure to the
          benefit of the parties, their successors, and permitted assigns.
        </p>

        <h3 className="terms-h3">13.5. Contact Information</h3>
        <p>
          If you have any questions about these Terms or the Services, please contact us at{" "}
          <a href="mailto:help@taranamusic.in" className="terms-link">
            help@taranamusic.in
          </a>
          .
        </p>
      </section>
    </LegalPageLayout>
  );
}
